import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';

type TabKey = 0 | 1 | 2;

type Props = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  displayName: string;
  avatarLetter: string;
  onLogout: () => void;
  onBackToSite: () => void;
  onOpenSettings?: () => void;
};

const AdminUserMenu: React.FC<Props> = ({
  anchorEl,
  open,
  onClose,
  displayName,
  avatarLetter,
  onLogout,
  onBackToSite,
  onOpenSettings,
}) => {
  const [tab, setTab] = React.useState<TabKey>(0);

  React.useEffect(() => {
    if (!open) setTab(0);
  }, [open]);

  const handleTabChange = (_: any, value: TabKey) => {
    setTab(value);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          width: 340,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 18px 60px rgba(2, 132, 199, 0.18)',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ px: 2, py: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main', width: 42, height: 42 }}>{avatarLetter}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }} noWrap>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              Admin
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          px: 1,
          '& .MuiTab-root': { minHeight: 44, fontWeight: 800 },
        }}
      >
        <Tab icon={<PersonRoundedIcon fontSize="small" />} iconPosition="start" label="Account" />
        <Tab icon={<ShieldRoundedIcon fontSize="small" />} iconPosition="start" label="Admin" />
        <Tab icon={<HelpOutlineRoundedIcon fontSize="small" />} iconPosition="start" label="Help" />
      </Tabs>

      <Divider />

      {tab === 0 && (
        <List dense disablePadding sx={{ py: 0.5 }}>
          <ListItemButton
            onClick={() => {
              onClose();
              onOpenSettings?.();
            }}
            disabled={!onOpenSettings}
            sx={{ px: 2, py: 1.1 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <SettingsRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={<Typography sx={{ fontWeight: 800 }}>Settings</Typography>}
              secondary="Website & platform preferences"
            />
          </ListItemButton>

          <ListItemButton
            onClick={() => {
              onClose();
              onBackToSite();
            }}
            sx={{ px: 2, py: 1.1 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <HomeRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={<Typography sx={{ fontWeight: 800 }}>Back to site</Typography>}
              secondary="Exit the admin dashboard"
            />
          </ListItemButton>
        </List>
      )}

      {tab === 1 && (
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              Quick actions
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                onClose();
                onLogout();
              }}
              startIcon={<LogoutRoundedIcon />}
              sx={{ justifyContent: 'flex-start', borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
              fullWidth
            >
              Logout
            </Button>
          </Stack>
        </Box>
      )}

      {tab === 2 && (
        <List dense disablePadding sx={{ py: 0.5 }}>
          <ListItemButton
            onClick={() => {
              onClose();
            }}
            sx={{ px: 2, py: 1.1 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <HelpOutlineRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={<Typography sx={{ fontWeight: 800 }}>Support</Typography>}
              secondary="Need help? Contact support from the main site."
            />
          </ListItemButton>
        </List>
      )}
    </Popover>
  );
};

export default AdminUserMenu;
